import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  candidatePrivacyNoticePath,
  candidateSessionPrivacyNoticePath,
  configureCandidateLinkApi,
} from '../services/candidateLinkRoute';
import {
  CandidateLinkShell,
  CandidateLinkSpinner,
  ExpiredLink,
} from '../components/CandidateLinkShell';
import {
  CandidateLink as LinkState,
  acceptConsent,
  getLink,
  RedeemedLinkSession,
  redeemLink,
  acceptSessionConsent,
  uploadSessionFiles,
  uploadFiles,
  RequestedDocument,
} from '../services/candidateLinks';

type Status = 'loading' | 'ready' | 'gone';

// Bumped whenever the notice itself changes, so a stored acceptance still says
// what was actually agreed to.
const PRIVACY_NOTICE_VERSION = '2026-09-18';

function browserSessionStorageKey(region: string | undefined): string {
  return `candidate-link-browser-session:${region || ''}`;
}

function savedBrowserSession(region: string | undefined): RedeemedLinkSession | null {
  try {
    const stored = sessionStorage.getItem(browserSessionStorageKey(region));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<RedeemedLinkSession>;
    return parsed.cookie_session === true
      && typeof parsed.csrf_token === 'string'
      && typeof parsed.session_id === 'string'
      ? {
          csrf_token: parsed.csrf_token,
          session_id: parsed.session_id,
          template_url: parsed.template_url,
          // The code is stripped from the URL once redeemed, so every reload comes
          // back through here. Dropping this loses the picker for the rest of the visit.
          requested: Array.isArray(parsed.requested) ? parsed.requested : [],
          cookie_session: true,
        }
      : null;
  } catch {
    return null;
  }
}

function saveBrowserSession(region: string | undefined, session: RedeemedLinkSession): void {
  sessionStorage.setItem(browserSessionStorageKey(region), JSON.stringify(session));
}

function codeFromFragment(): string | undefined {
  const fragment = window.location.hash.slice(1);
  if (!fragment) return undefined;
  try {
    return decodeURIComponent(fragment);
  } catch {
    return undefined;
  }
}

/**
 * The one page a candidate opens from a link in a message.
 *
 * Candidates can send the documents their application needs here. When an
 * employer provides a template, they can download it, complete it away from
 * the page, and upload it with any supporting documents.
 *
 * Consent sits above the upload rather than on a screen of its own. The page is
 * short enough to hold both, and on a phone that is one fewer tap before anyone
 * can start.
 */
/**
 * What to put on screen when a send fails.
 *
 * The server's own wording is fit to read for the refusals a candidate can act
 * on, a file too large or of the wrong kind. A failed bot check is not one of
 * those: its message names reCAPTCHA and a missing token, which tells the
 * candidate nothing they can do and reads like something broke on our side.
 */
function readableFailure(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && !/recaptcha/i.test(detail)) return detail;
  if (typeof detail === 'string') {
    return 'We could not verify this device. Please reload the page and try again.';
  }
  return 'We could not send that. Please try again.';
}

const CandidateLink: React.FC = () => {
  const { region, code: pathCode } = useParams<{ region: string; code: string }>();
  const code = pathCode || codeFromFragment();

  const [status, setStatus] = useState<Status>('loading');
  const [state, setState] = useState<LinkState | null>(null);
  const [cookieSession, setCookieSession] = useState<RedeemedLinkSession | null>(null);
  const [sent, setSent] = useState(false);
  // The write-only session never reads back what arrived, so a document the
  // candidate just sent is remembered here for the rest of this visit.
  const [sentKeys, setSentKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!configureCandidateLinkApi(region)) {
      setStatus('gone');
      return;
    }
    if (!code) {
      const saved = savedBrowserSession(region);
      if (saved) {
        setCookieSession(saved);
        setStatus('ready');
      } else {
        setStatus('gone');
      }
      return;
    }
    try {
      const redeemed = await redeemLink(code);
      if (!redeemed.cookie_session) {
        // The API explicitly says this client has not rolled out browser sessions.
        // Do not retry the legacy code-read endpoint after any other failure.
        setState(await getLink(code));
        setStatus('ready');
        return;
      }
      saveBrowserSession(region, redeemed);
      setCookieSession(redeemed);
      // The code was only an exchange credential. Remove it before the page
      // renders links or loads anything else that could carry a referrer.
      window.history.replaceState(null, '', `/candidate/${encodeURIComponent(region || '')}`);
      setStatus('ready');
      return;
    } catch {
      // Expired, revoked, network and server errors all fail closed. In
      // particular, do not send the code to a legacy read endpoint here.
      setStatus('gone');
    }
  }, [code, region]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (files: FileList | null, key?: string) => {
    if ((!cookieSession && !code) || !files || files.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Consent is recorded on the first send rather than behind its own button:
      // the notice is on screen above, and agreeing to it is what sending means.
      if (cookieSession) {
        await acceptSessionConsent(cookieSession, PRIVACY_NOTICE_VERSION);
        await uploadSessionFiles(cookieSession, Array.from(files), key);
        setSent(true);
        if (key) setSentKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
        return;
      }
      const legacyCode = code;
      if (!legacyCode) return;
      const agreed = state?.consent_given
        ? state
        : await acceptConsent(legacyCode, PRIVACY_NOTICE_VERSION);
      setState(agreed);
      setState(await uploadFiles(legacyCode, Array.from(files), key));
    } catch (err: any) {
      // The server explains a refused file in words fit to read on a phone.
      setError(readableFailure(err));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  // Configuration, from whichever bootstrap this route used. Empty means the client
  // asks for one thing and there is nothing to choose between.
  const requested: RequestedDocument[] =
    state?.requested ?? cookieSession?.requested ?? [];
  const filesFor = (key: string) =>
    (state?.sent ?? []).filter((file) => file.key === key);
  const isSent = (key: string) =>
    sentKeys.includes(key) || filesFor(key).length > 0;

  if (status === 'loading') {
    return (
      <CandidateLinkShell>
        <CandidateLinkSpinner />
      </CandidateLinkShell>
    );
  }

  if (status === 'gone' || (!state && !cookieSession)) {
    return <ExpiredLink code={code!} />;
  }

  return (
    <CandidateLinkShell>
      <h1 className="text-2xl">Send your documents</h1>
      <p className="mt-3 text-base" style={{ color: 'var(--link-ink-soft)' }}>
        Send the documents requested for your application, plus anything else you
        think is relevant. You can add more later while this link is open.
      </p>

      {(state?.template_url || cookieSession?.template_url) && (
        <a
          href={state?.template_url || cookieSession?.template_url || undefined}
          target="_blank"
          rel="noreferrer"
          className="mt-6 flex items-center gap-3 rounded-xl px-4 py-3.5 text-base"
          style={{ border: '1px solid var(--link-rule)', color: 'var(--link-ink)' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            Download the document template
            <span className="block text-xs" style={{ color: 'var(--link-ink-soft)' }}>
              Fill it in, then send it back below
            </span>
          </span>
        </a>
      )}

      {!cookieSession && requested.length === 0 && state && state.sent.length > 0 && (
        <div className="mt-8">
          <p
            className="text-xs font-medium uppercase tracking-widest"
            style={{ color: 'var(--link-ink-soft)' }}
          >
            Sent so far
          </p>
          <ul className="mt-3 space-y-2">
            {state.sent.map((file) => (
              <li
                key={file.uuid}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
                style={{ background: 'var(--link-good-ground)', color: 'var(--link-good)' }}
              >
                <span aria-hidden>✓</span>
                <span className="truncate">{file.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="mt-4 text-sm" style={{ color: '#a33' }}>
          {error}
        </p>
      )}

      {requested.length > 0 ? (
        <div className="mt-8 space-y-3">
          {requested.map((doc) => (
            <div
              key={doc.key}
              className="rounded-xl px-4 py-4"
              style={{ border: '1px solid var(--link-rule)' }}
            >
              <p className="text-base" style={{ color: 'var(--link-ink)' }}>
                {doc.label}
              </p>
              {isSent(doc.key) && (
                <ul className="mt-2 space-y-1" style={{ color: 'var(--link-good)' }}>
                  {filesFor(doc.key).length > 0 ? (
                    filesFor(doc.key).map((file) => (
                      <li key={file.uuid} className="flex items-center gap-2 text-sm">
                        <span aria-hidden>✓</span>
                        <span className="truncate">{file.name}</span>
                      </li>
                    ))
                  ) : (
                    <li className="flex items-center gap-2 text-sm">
                      <span aria-hidden>✓</span>
                      <span>Sent</span>
                    </li>
                  )}
                </ul>
              )}
              <input
                id={`documents-${doc.key}`}
                type="file"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const input = event.target;
                  send(input.files, doc.key).finally(() => {
                    input.value = '';
                  });
                }}
              />
              <label
                htmlFor={`documents-${doc.key}`}
                className="orbio-link-button mt-3 block w-full cursor-pointer rounded-xl px-4 py-3 text-center text-base font-medium"
                style={busy ? { background: 'var(--link-action-muted)' } : undefined}
              >
                {busy ? 'Sending…' : isSent(doc.key) ? 'Add more' : 'Choose files'}
              </label>
            </div>
          ))}
        </div>
      ) : (
        <>
          <input
            ref={fileInput}
            id="documents"
            type="file"
            multiple
            className="sr-only"
            onChange={(event) => send(event.target.files)}
          />
          <label
            htmlFor="documents"
            className="orbio-link-button mt-8 block w-full cursor-pointer rounded-xl px-4 py-3.5 text-center text-base font-medium"
            style={busy ? { background: 'var(--link-action-muted)' } : undefined}
          >
            {busy ? 'Sending…' : sent || state?.sent.length ? 'Add more' : 'Choose files'}
          </label>
        </>
      )}
      <p className="mt-3 text-center text-xs" style={{ color: 'var(--link-ink-soft)' }}>
        PDFs or photos, up to 20MB each.
      </p>

      {(!cookieSession && !state?.consent_given) && (
        <p className="mt-6 text-xs" style={{ color: 'var(--link-ink-soft)' }}>
          By sending, you agree that your documents are stored securely and shared
          only with the employer you applied to. Nothing you send here goes anywhere
          else.{' '}
          <a
            href={candidatePrivacyNoticePath(region, code!)}
            className="underline"
            style={{ color: 'var(--link-action)' }}
            target="_blank"
            rel="noreferrer"
          >
            Read the privacy notice
          </a>
          .
        </p>
      )}
      {cookieSession && (
        <p className="mt-6 text-xs" style={{ color: 'var(--link-ink-soft)' }}>
          By sending, you agree that your documents are stored securely and shared
          only with the employer you applied to.{' '}
          <a
            href={candidateSessionPrivacyNoticePath(region)}
            className="underline"
            style={{ color: 'var(--link-action)' }}
            target="_blank"
            rel="noreferrer"
          >
            Read the privacy notice
          </a>
          .
        </p>
      )}
    </CandidateLinkShell>
  );
};

export default CandidateLink;
